/**
 * Per-marker position interpolator — bridges the gap between sparse MQTT
 * ticks (e.g. 4 Hz drone telemetry, 1 Hz target updates) and the map's
 * render loop (60 Hz). Without this, markers visibly jump on each tick;
 * with it, motion is smooth and matches the v1 surveillance aircraft
 * pattern (`aircraftStore.ts` 30fps rAF + dead-reckoning).
 *
 * Usage:
 *   const interp = new PositionInterpolator(tickMs);
 *   interp.target(id, lng, lat);     // call on each MQTT envelope
 *   interp.read(id);                 // returns interpolated [lng, lat] for now()
 *
 * The marker subscriber drives a rAF loop and writes `setLngLat(read(id))`
 * once per frame. When the user starts a zoom/pan, the subscriber holds
 * the previous interpolated value (see mapAnimation in instance.ts).
 */

interface Track {
  fromLng: number;
  fromLat: number;
  toLng: number;
  toLat: number;
  /** ms epoch when the segment started. */
  startedAt: number;
}

export class PositionInterpolator {
  /** Target tick duration in ms — interpolation completes at this point. */
  private tickMs: number;
  private tracks = new Map<string, Track>();

  constructor(tickMs: number) {
    this.tickMs = tickMs;
  }

  /** Record the latest known position. Begins a fresh tween from the
   * previous interpolated point. */
  target(id: string, lng: number, lat: number, now = performance.now()): void {
    const prev = this.tracks.get(id);
    if (!prev) {
      this.tracks.set(id, { fromLng: lng, fromLat: lat, toLng: lng, toLat: lat, startedAt: now });
      return;
    }
    const cur = this.read(id, now);
    this.tracks.set(id, { fromLng: cur[0], fromLat: cur[1], toLng: lng, toLat: lat, startedAt: now });
  }

  /** Return [lng, lat] linearly interpolated for the current frame. */
  read(id: string, now = performance.now()): [number, number] {
    const t = this.tracks.get(id);
    if (!t) return [0, 0];
    const elapsed = now - t.startedAt;
    const k = Math.min(1, elapsed / this.tickMs);
    return [
      t.fromLng + (t.toLng - t.fromLng) * k,
      t.fromLat + (t.toLat - t.fromLat) * k,
    ];
  }

  /** Drop an id (target left feed). */
  delete(id: string): void {
    this.tracks.delete(id);
  }

  /** Drop all ids not in `keep`. */
  retain(keep: Set<string>): void {
    for (const id of this.tracks.keys()) {
      if (!keep.has(id)) this.tracks.delete(id);
    }
  }

  /** Convenience: number of tracked ids. */
  size(): number {
    return this.tracks.size;
  }
}
